/**
 * Global Error Handlers
 *
 * Sets up global error handling for unhandled errors and promise rejections
 */

import { errorLogger } from '@shared/services/ErrorLogger';
import { ContextCollector } from '@shared/utils/errorUtils';

/**
 * Sets up global error handlers
 */
export function setupGlobalErrorHandlers(): void {
  // Handle uncaught JavaScript errors
  window.addEventListener('error', event => {
    const contextualError = errorLogger.createContextualError(
      `Global error: ${event.message}`,
      'global_error',
      event.error,
      {
        url: event.filename,
        metadata: {
          lineno: event.lineno,
          colno: event.colno,
          source: event.filename,
        },
      }
    );

    errorLogger.logError(contextualError);
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    const contextualError = errorLogger.createContextualError(
      `Unhandled promise rejection: ${error.message}`,
      'unhandled_promise_rejection',
      error,
      {
        metadata: {
          reason: event.reason,
          promise: event.promise,
        },
      }
    );

    errorLogger.logError(contextualError);
  });

  // Track navigation for breadcrumbs
  if (typeof window !== 'undefined' && window.history) {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      const breadcrumb = ContextCollector.createBreadcrumb(
        `Navigation: ${window.location.pathname} -> ${args[2]}`,
        'navigation',
        'info',
        { method: 'pushState', url: args[2] }
      );
      errorLogger.addBreadcrumb(breadcrumb);

      return originalPushState.apply(this, args);
    };

    window.history.replaceState = function (...args) {
      const breadcrumb = ContextCollector.createBreadcrumb(
        `Navigation: ${window.location.pathname} -> ${args[2]}`,
        'navigation',
        'info',
        { method: 'replaceState', url: args[2] }
      );
      errorLogger.addBreadcrumb(breadcrumb);

      return originalReplaceState.apply(this, args);
    };

    // Track back/forward navigation
    window.addEventListener('popstate', () => {
      const breadcrumb = ContextCollector.createBreadcrumb(
        `Navigation: popstate to ${window.location.pathname}`,
        'navigation',
        'info',
        { method: 'popstate', url: window.location.pathname }
      );
      errorLogger.addBreadcrumb(breadcrumb);
    });
  }

  // Track console errors for additional context
  if (typeof console !== 'undefined') {
    const originalConsoleError = console.error;
    console.error = function (...args) {
      const breadcrumb = ContextCollector.createBreadcrumb(
        `Console error: ${args.join(' ')}`,
        'error',
        'error',
        { args }
      );
      errorLogger.addBreadcrumb(breadcrumb);

      return originalConsoleError.apply(this, args);
    };
  }
}

/**
 * Removes global error handlers (useful for cleanup in tests)
 */
export function removeGlobalErrorHandlers(): void {
  // Note: This is a simplified cleanup. In a real implementation,
  // you'd want to store references to the original handlers and restore them
  window.removeEventListener('error', () => {});
  window.removeEventListener('unhandledrejection', () => {});
  window.removeEventListener('popstate', () => {});
}
